import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Button, Input, Tag, Table, Typography, Space,
  Alert, App as AntApp, Flex,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import type { Usuario, Grupo } from './types';
import { STATUS_LABEL } from './types';
import { getToken, fmtDate } from './utils';
import FormNovo from './FormNovo';
import FormGrupos from './FormGrupos';

const { Title, Text } = Typography;

// Lista + edição de usuários Cognito.
export default function AdminUsuarios() {
  const { message, modal } = AntApp.useApp();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState<Usuario | null>(null);
  const [mostrarNovo, setMostrarNovo] = useState(false);
  const [busca, setBusca] = useState('');
  const [meuEmail, setMeuEmail] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const email = (session.tokens?.idToken?.payload as any)?.email || '';
        setMeuEmail(String(email).toLowerCase());
      } catch {}
    })();
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const [uRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/admin/usuarios`, { headers: { Authorization: token } }),
        axios.get(`${API_URL}/admin/perfis`,   { headers: { Authorization: token } }),
      ]);
      const u = uRes.data.body ? JSON.parse(uRes.data.body) : uRes.data;
      const p = pRes.data.body ? JSON.parse(pRes.data.body) : pRes.data;
      setUsuarios(u);
      setGrupos((Array.isArray(p) ? p : []).map((perfil: any) => ({
        name: perfil.nome, description: perfil.descricao || '',
      })));
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    } finally { setLoading(false); }
  }

  const eEuMesmo = (u: Usuario) => (u.email || '').toLowerCase() === meuEmail;

  async function resetarSenha(u: Usuario) {
    const senha = prompt(`Nova senha temporária para ${u.email}:\n(mínimo 8 chars, maiúscula, minúscula, número, especial)`, 'Hydrodata@2026!');
    if (!senha) return;
    try {
      const token = await getToken();
      await axios.patch(`${API_URL}/admin/usuarios/${encodeURIComponent(u.email)}`,
        { acao: 'reset_senha', senha_temp: senha }, { headers: { Authorization: token } });
      alert(`Senha resetada. Informe ao usuário: ${senha}`);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    }
  }

  function togglar(u: Usuario) {
    modal.confirm({
      title: `${u.enabled ? 'Desativar' : 'Reativar'} usuário ${u.email}?`,
      okText: u.enabled ? 'Desativar' : 'Reativar', cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.patch(`${API_URL}/admin/usuarios/${encodeURIComponent(u.email)}`,
            { acao: 'toggle', enabled: !u.enabled }, { headers: { Authorization: token } });
          await carregar();
        } catch (e: any) {
          // 409 use_desligar: pessoa com ficha se desliga pela ficha de RH (pede a data).
          message.error(e.response?.data?.erro || e.response?.data?.error || e.message, 8);
        }
      },
    });
  }

  function excluir(u: Usuario) {
    modal.confirm({
      title: `⚠ Excluir DEFINITIVAMENTE o usuário ${u.email}?`,
      content: 'Esta ação não pode ser desfeita.',
      okText: 'Excluir', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/admin/usuarios/${encodeURIComponent(u.email)}`,
            { headers: { Authorization: token } });
          await carregar();
        } catch (e: any) { message.error('Erro: ' + e.message); }
      },
    });
  }

  const filtrados = usuarios.filter(u => !busca ||
    u.email.toLowerCase().includes(busca.toLowerCase()) || u.nome.toLowerCase().includes(busca.toLowerCase()));

  const cols: ColumnsType<Usuario> = [
    {
      title: 'Nome', dataIndex: 'nome', key: 'n',
      render: (v) => <Text strong style={{ color: '#020D7F' }}>{v || '—'}</Text>,
    },
    { title: 'E-mail', dataIndex: 'email', key: 'e', render: (v) => <Text style={{ color: '#555' }}>{v}</Text> },
    {
      title: 'Perfis', dataIndex: 'groups', key: 'g', width: 200,
      render: (gs: string[]) => gs.length === 0
        ? <Text type="secondary" italic>sem grupos</Text>
        : <Space size={4} wrap>{gs.map(g => <Tag key={g} color="blue">{g}</Tag>)}</Space>,
    },
    {
      title: 'Status', key: 'st', width: 130, align: 'center',
      render: (_, u) => u.enabled
        ? <Tag color={u.status === 'CONFIRMED' ? 'success' : 'gold'}>{STATUS_LABEL[u.status] || u.status}</Tag>
        : <Tag color="default">DESATIVADO</Tag>,
    },
    { title: 'Criado', dataIndex: 'criado_em', key: 'c', width: 100, align: 'center',
      render: (v) => <Text style={{ fontSize: 11 }}>{fmtDate(v)}</Text> },
    {
      title: 'Ações', key: 'ac', width: 240, align: 'center',
      render: (_, u) => (
        <Space size={2}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(u)} title="Editar" />
          {eEuMesmo(u)
            ? <Text type="secondary" italic style={{ fontSize: 10 }}>você</Text>
            : <>
                <Button size="small" onClick={() => resetarSenha(u)}
                  style={{ color: '#B07500', borderColor: '#B07500', fontSize: 10 }}>Reset</Button>
                <Button size="small" onClick={() => togglar(u)} style={{ fontSize: 10 }}>
                  {u.enabled ? 'Desativar' : 'Ativar'}
                </Button>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => excluir(u)} />
              </>}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Flex align="center" gap={12} style={{ marginBottom: 14 }} wrap="wrap">
        <Title level={5} style={{ margin: 0 }}>Usuários</Title>
        <Input placeholder="Buscar por nome ou email..." value={busca} onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />} size="small"
          allowClear style={{ maxWidth: 320, flex: 1 }} />
        <Button type="primary" icon={<PlusOutlined />} style={{ marginLeft: 'auto' }} onClick={() => setMostrarNovo(true)}>
          Novo usuário
        </Button>
      </Flex>

      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message={<Text style={{ fontSize: 11 }}><strong>Perfis disponíveis:</strong> {grupos.map(g => g.name).join(' · ')}</Text>}
      />

      <Table rowKey="username" columns={cols} dataSource={filtrados} loading={loading}
        size="small" pagination={{ pageSize: 50, showSizeChanger: true }}
        rowClassName={(u) => !u.enabled ? 'row-fechado' : ''} />

      {mostrarNovo && <FormNovo grupos={grupos} onClose={() => setMostrarNovo(false)} onSaved={carregar} />}
      {editando && <FormGrupos usuario={editando} grupos={grupos} eEuMesmo={eEuMesmo(editando)}
        onClose={() => setEditando(null)} onSaved={carregar} />}
    </div>
  );
}
